import React from 'react';
import { PencilRulerIcon } from '@phosphor-icons/react';
import { DestaquesCarrossel } from './DestaquesCarrossel';
import {
  toDestaqueCarrosselCards,
  type DestaquesCarrosselData,
} from './helpers';
import { TITULO_FAIXA } from './typography';

interface DestaquesCarrosselSectionProps {
  data?: DestaquesCarrosselData;
  isEditMode?: boolean;
  /** Presente só no editor: recebe o índice do slide em `data.items`. */
  onEditItem?: (index: number) => void;
}

export const DESTAQUES_CARROSSEL_TITLE = 'Destaques';

/**
 * Faixa "Destaques" (bloco `mpfDestaquesCarrossel`).
 *
 * Coluna estreita ao lado do mosaico de iniciativas, com um destaque por vez.
 * Sem curadoria a faixa não é renderizada — não há fallback automático.
 */
export const DestaquesCarrosselSection: React.FC<
  DestaquesCarrosselSectionProps
> = ({ data, isEditMode, onEditItem }) => {
  const cards = React.useMemo(
    () => toDestaqueCarrosselCards(data?.items),
    [data],
  );

  if (cards.length === 0) return null;

  return (
    /* Mesma moldura das outras faixas: traço, raio 16 e padding 16, com os 16
       entre cabeçalho e carrossel saindo do `gap`. `h-full` para terminar na
       mesma linha do mosaico ao lado.
       `not-prose` porque a seção também é usada como bloco numa página comum,
       onde o `prose` do corpo do conteúdo vazaria para dentro — a explicação
       inteira está em `AcompanheRedesSection`. */
    <section
      aria-labelledby="capa-destaques-carrossel-titulo"
      className="not-prose flex h-full flex-col gap-4 rounded-2xl border border-gray-200 bg-surface-container-lowest p-4 dark:border-night-850 dark:bg-night-800"
    >
      <div className="flex items-center gap-2">
        {/* Verde do design (#057A55), não o `green-600` do Tailwind (#16A34A):
            a paleta `green` dele não é redirecionada para os tokens. */}
        <PencilRulerIcon
          aria-hidden="true"
          className="h-6 w-6 shrink-0 text-[color:var(--green-600)] dark:text-white"
        />
        <h3
          id="capa-destaques-carrossel-titulo"
          className={`${TITULO_FAIXA} text-blue-800 dark:text-white`}
        >
          {data?.title || DESTAQUES_CARROSSEL_TITLE}
        </h3>
      </div>

      {/* Régua na mesma cor do ícone: decorativa, não separa conteúdo
          semântico — o cabeçalho já é o rótulo da seção. */}
      <div
        aria-hidden="true"
        className="h-0.5 w-full shrink-0 bg-[color:var(--green-600)] dark:!bg-night-750"
      />

      <DestaquesCarrossel
        cards={cards}
        autoplay={data?.autoplay}
        autoplayDelay={data?.autoplayDelay}
        isEditMode={isEditMode}
        onEditItem={onEditItem}
      />
    </section>
  );
};

export default DestaquesCarrosselSection;
